import { queryOptions, keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProposalsOverviewsDeleteProposalRequest, ProposalsOverviewsSearchRequest } from 'bff';
import { secondsToMilliseconds } from 'date-fns';
import { displayToast } from '@sanlam-glacier/app-common/modules/toasts';
import { clientService } from 'stores/client-store/client-store';
const proposalOverviewPrefix = 'proposalOverview';

export const useInvalidateGetAllProposals = () => {
  const queryClient = useQueryClient();

  return async () =>
    await queryClient.invalidateQueries({
      queryKey: [proposalOverviewPrefix, 'useSearchProposals']
    });
};

/**
 * Search proposals on Proposal Overview
 *
 * @param filters The filters for the Query
 * @param options Additional options to pass through to the query hook
 * @returns A Result object with the list of all proposal
 */
export const getProposalOverviewOptions = (
  searchProposalsRequest: ProposalsOverviewsSearchRequest
) => {
  const { proposalsOverviewsApi } = clientService.getApiClient();

  return queryOptions({
    meta: {
      errorMessage:
        'There was an error fetching your list of proposals. Please check your network connection and try again.'
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: secondsToMilliseconds(5),
    throwOnError: true,
    placeholderData: keepPreviousData,
    queryKey: [proposalOverviewPrefix, 'useSearchProposals', searchProposalsRequest],
    queryFn: () => proposalsOverviewsApi.proposalsOverviewsSearch(searchProposalsRequest)
  });
};

/**
 * Delete proposal from Proposals
 *
 * @param ProposalsOverviewsDeleteProposalRequest Proposal Id
 */
export const useDeleteProposal = (
  deleteProposalRequest: ProposalsOverviewsDeleteProposalRequest
) => {
  const { proposalsOverviewsApi } = clientService.getApiClient();
  const invalidateQuery = useInvalidateGetAllProposals();

  return useMutation({
    mutationKey: [proposalOverviewPrefix + 'useDeleteProposal', deleteProposalRequest],
    mutationFn: (request: ProposalsOverviewsDeleteProposalRequest) =>
      proposalsOverviewsApi.proposalsOverviewsDeleteProposal(request),
    onSuccess: async () => {
      displayToast({
        toastId: 'Delete proposal',
        title: 'Proposal deleted',
        message: `Deleted proposal #${deleteProposalRequest.deleteProposalCommand.id}`,
        status: 'success',
        options: {
          disableTimeout: false
        }
      });
      await invalidateQuery();
    }
  });
};
